import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase, ADMIN_EMAIL } from '../lib/supabase'
import { reportLoginFailure } from '../lib/reportLoginFailure'
import { formatOtpAuthError, otpAuthApi } from '../api/otpAuth'
import { Lock, ShieldCheck } from 'lucide-react'

type Step = 'password' | 'totp'

export default function LoginPage() {
  const navigate = useNavigate()
  const [remember, setRemember] = useState(false)
  const [step, setStep] = useState<Step>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [loginAttempt, setLoginAttempt] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user?.email === ADMIN_EMAIL) navigate('/')
    })
  }, [navigate])

  const finishIfAdmin = async () => {
    const { data } = await supabase.auth.getUser()
    if (!data.user || data.user.email !== ADMIN_EMAIL) {
      await supabase.auth.signOut()
      throw new Error('Access denied. Admin only.')
    }
    navigate('/')
  }

  const handlePassword = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      if (email.trim().toLowerCase() !== ADMIN_EMAIL) {
        reportLoginFailure(email, 'admin')
        setError('Access denied. Admin only.')
        return
      }
      const result = await otpAuthApi.passwordVerify(email.trim(), password)
      if (result.next !== 'totp') {
        // Admin must use the JumpStart authenticator Nizan already enrolled.
        // Do not fall back to password-only or email OTP.
        throw new Error('totp_required')
      }
      setLoginAttempt(result.login_attempt)
      setTotpCode('')
      setStep('totp')
    } catch (err) {
      const code = err instanceof Error ? err.message : 'invalid_credentials'
      if (code === 'invalid_credentials' || code === 'password_required') {
        reportLoginFailure(email, 'admin')
      }
      setError(formatOtpAuthError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleTotp = async (e: FormEvent) => {
    e.preventDefault()
    if (!/^\d{6}$/.test(totpCode)) {
      setError('Enter the 6-digit code from your authenticator.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await otpAuthApi.verify(loginAttempt, totpCode, remember)
      await finishIfAdmin()
    } catch (err) {
      const code = err instanceof Error ? err.message : 'invalid_code'
      if (code === 'Access denied. Admin only.') {
        setError(code)
      } else {
        setError(formatOtpAuthError(err))
      }
      setTotpCode('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0f1117' }}>
      <div className="w-full max-w-md rounded-2xl p-8" style={{ background: '#1a1d27', border: '1px solid #2d3148' }}>
        <div className="text-center mb-8">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4"
            style={{ background: '#6366f1' }}
          >
            O
          </div>
          <h1 className="text-2xl font-bold text-white">Otomator Admin</h1>
          <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>
            {step === 'password' ? 'Sign in to continue' : 'Authenticator code'}
          </p>
        </div>

        {step === 'password' && (
          <form onSubmit={handlePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#d1d5db' }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                placeholder="admin@example.com"
                className="w-full px-4 py-2.5 rounded-lg text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-indigo-500 transition"
                style={{ background: '#252836', border: '1px solid #3f4258' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#d1d5db' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full px-4 py-2.5 rounded-lg text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-indigo-500 transition"
                style={{ background: '#252836', border: '1px solid #3f4258' }}
              />
            </div>
            <label dir="rtl" className="flex min-h-11 items-center gap-3 text-sm text-gray-300">
              <input type="checkbox" checked={remember} onChange={event => setRemember(event.target.checked)} className="h-5 w-5" disabled={loading} />
              שמור אותי מחובר לשבוע במכשיר הזה
            </label>
            {error && (
              <div
                className="px-4 py-3 rounded-lg text-sm"
                style={{ background: '#ef444420', color: '#f87171', border: '1px solid #ef444440' }}
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg font-semibold text-white transition flex items-center justify-center gap-2 mt-2"
              style={{ background: '#6366f1', opacity: loading ? 0.7 : 1 }}
            >
              <Lock size={16} />
              {loading ? 'Verifying…' : 'Continue'}
            </button>
          </form>
        )}

        {step === 'totp' && (
          <form onSubmit={handleTotp} className="space-y-4">
            <p className="text-sm" style={{ color: '#9ca3af' }}>
              Enter the 6-digit code from the same authenticator you use for JumpStart Hub.
            </p>
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#d1d5db' }}>
                Authenticator code
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                autoFocus
                placeholder="000000"
                dir="ltr"
                className="w-full px-4 py-2.5 rounded-lg text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-indigo-500 transition tracking-[0.4em] text-center text-lg font-mono"
                style={{ background: '#252836', border: '1px solid #3f4258' }}
              />
            </div>
            {error && (
              <div
                className="px-4 py-3 rounded-lg text-sm"
                style={{ background: '#ef444420', color: '#f87171', border: '1px solid #ef444440' }}
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading || totpCode.length !== 6}
              className="w-full py-2.5 rounded-lg font-semibold text-white transition flex items-center justify-center gap-2 mt-2"
              style={{ background: '#6366f1', opacity: loading || totpCode.length !== 6 ? 0.7 : 1 }}
            >
              <ShieldCheck size={16} />
              {loading ? 'Verifying…' : 'Verify and sign in'}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={() => {
                setStep('password')
                setLoginAttempt('')
                setTotpCode('')
                setError('')
                setPassword('')
              }}
              className="w-full py-2 text-sm transition"
              style={{ color: '#9ca3af' }}
            >
              ← Back to password
            </button>
          </form>
        )}

        <div className="text-center mt-6">
          <a
            href="/whatsapp/pricing"
            className="text-sm transition-colors hover:text-white"
            style={{ color: '#6b7280' }}
          >
            שירות WhatsApp — תמחור ורכישה →
          </a>
        </div>
      </div>
    </div>
  )
}
